import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Music, Wine, Briefcase, Users } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingModal from '../../components/BookingModal';

const AfterWork = () => {
  const { language } = useLanguage();
  const [afterworks, setAfterworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedAfterwork, setSelectedAfterwork] = useState(null);

  useEffect(() => {
    fetchAfterWorks();
  }, []);

  const fetchAfterWorks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/afterworks`);
      if (response.ok) {
        const data = await response.json();
        setAfterworks(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des after-works:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get image URL (handle both external URLs and uploaded images)
  const getImageUrl = (image) => {
    if (!image) return 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=600&q=80';
    if (image.startsWith('http')) return image;
    const apiPath = image.replace('/uploads/', '/api/uploads/');
    return `${process.env.REACT_APP_BACKEND_URL}${apiPath}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '100px 0', textAlign: 'center' }}>
        <p className="body-regular">{language === 'fr' ? 'Chargement des After-Works...' : 'Loading After-Works...'}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-light)',
        padding: '32px 40px'
      }}>
        <div className="container" style={{ maxWidth: '1400px' }}>
          <Link to="/member/dashboard" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '14px'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
          </Link>
          <h1 className="hero-medium">
            {language === 'fr' ? 'After-Work H&G' : 'H&G After-Work'}
          </h1>
          <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            {language === 'fr' 
              ? 'Rendez-vous exclusifs pour membres du Club Henry & Gaby' 
              : 'Exclusive meetings for Henry & Gaby Club members'}
          </p>
        </div>
      </div>

      {/* Events List */}
      <section className="section-padding">
        <div className="container">
          {afterworks.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {language === 'fr' ? 'Aucun After-Work disponible pour le moment' : 'No After-Work available at the moment'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
              gap: '32px'
            }}>
              {afterworks.map((event) => {
                const availableSeats = event.total_seats - event.seats_sold;
                const isAvailable = event.status === 'Disponible' && availableSeats > 0;
                
                return (
                  <div
                    key={event.id}
                    className="hover-lift"
                    style={{
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-light)',
                      overflow: 'hidden',
                      position: 'relative',
                      opacity: event.status === 'Annulé' ? 0.7 : 1
                    }}
                  >
                    {/* Event Image */}
                    <div style={{ position: 'relative' }}>
                      <img
                        src={getImageUrl(event.image)}
                        alt={language === 'fr' ? event.title : (event.title_en || event.title)}
                        style={{
                          width: '100%',
                          height: '280px',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Type Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        padding: '6px 12px',
                        background: '#8b4f6d',
                        color: 'var(--bg-primary)',
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}>
                        After-Work
                      </div>

                      {/* Status Badge */}
                      {event.status !== 'Disponible' && (
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          left: '16px',
                          padding: '6px 12px',
                          background: event.status === 'Complet' ? '#c41e3a' : '#999',
                          color: 'var(--bg-primary)',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase'
                        }}>
                          {event.status === 'Complet' 
                            ? (language === 'fr' ? 'Complet' : 'Sold Out')
                            : (language === 'fr' ? 'Annulé' : 'Cancelled')
                          }
                        </div>
                      )}
                    </div>

                    {/* Event Details */}
                    <div style={{ padding: '28px' }}>
                      {/* Subtitle */}
                      {event.subtitle && (
                        <div style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-light)',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          marginBottom: '12px',
                          color: '#8b4f6d'
                        }}>
                          {language === 'fr' ? event.subtitle : (event.subtitle_en || event.subtitle)}
                        </div>
                      )}

                      {/* Date */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                      }}>
                        <Calendar size={18} color="var(--text-secondary)" />
                        <span className="body-regular" style={{ 
                          color: 'var(--text-secondary)',
                          fontWeight: 600 
                        }}>
                          {event.date}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="heading-1" style={{ 
                        marginBottom: '12px',
                        lineHeight: 1.3
                      }}>
                        {language === 'fr' ? event.title : (event.title_en || event.title)}
                      </h3>

                      {/* Location */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px'
                      }}>
                        <MapPin size={16} color="var(--text-secondary)" />
                        <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                          {event.location}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="body-regular" style={{ 
                        color: 'var(--text-secondary)',
                        lineHeight: 1.7,
                        marginBottom: '20px',
                        paddingBottom: '20px',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        {language === 'fr' ? event.description : (event.description_en || event.description)}
                      </p>

                      {/* Features */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Wine size={16} color="#8b4f6d" />
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                            Champagne & Caviar
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Music size={16} color="#8b4f6d" />
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                            Jazz Live
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Briefcase size={16} color="#8b4f6d" />
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                            Networking
                          </span>
                        </div>
                        {event.atmosphere && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '16px' }}>🎩</span>
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {language === 'fr' ? event.atmosphere : (event.atmosphere_en || event.atmosphere)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Availability */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                      }}>
                        <Users size={18} color="var(--text-secondary)" />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {availableSeats}/{event.total_seats} {language === 'fr' ? 'places disponibles' : 'seats available'}
                        </span>
                      </div>

                      {/* Pricing */}
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        marginBottom: '16px',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-light)'
                      }}>
                        <div>
                          <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {language === 'fr' ? 'Prix membre' : 'Member price'}
                          </p>
                          <p className="body-regular" style={{ fontWeight: 700, color: '#8b4f6d' }}>
                            {event.price_member > 0 
                              ? `${event.price_member?.toLocaleString('fr-FR')} €`
                              : (language === 'fr' ? 'Gratuit' : 'Free')
                            }
                          </p>
                        </div>
                        {event.price_non_member > 0 && (
                          <div>
                            <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                              {language === 'fr' ? 'Prix non-membre' : 'Non-member price'}
                            </p>
                            <p className="body-regular" style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                              {event.price_non_member?.toLocaleString('fr-FR')} €
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {event.detail_link && (
                          <Link 
                            to={event.detail_link}
                            className="btn-secondary" 
                            style={{ 
                              flex: 1,
                              justifyContent: 'center',
                              textDecoration: 'none',
                              display: 'flex'
                            }}
                          >
                            {language === 'fr' ? 'Détails' : 'Details'}
                          </Link>
                        )}
                        <button 
                          className="btn-primary" 
                          style={{ 
                            flex: 1,
                            justifyContent: 'center',
                            opacity: isAvailable ? 1 : 0.6
                          }}
                          disabled={!isAvailable}
                          onClick={() => {
                            setSelectedAfterwork(event);
                            setBookingModalOpen(true);
                          }}
                        >
                          {isAvailable 
                            ? (language === 'fr' ? 'Réserver' : 'Book now')
                            : (language === 'fr' ? 'Complet' : 'Sold out')
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px', textAlign: 'center' }}>
          <h3 className="heading-1" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'After-Work H&G' : 'H&G After-Work'}
          </h3>
          <p className="body-regular" style={{ 
            color: 'var(--text-secondary)', 
            lineHeight: 1.8 
          }}>
            {language === 'fr'
              ? "Les After-Work H&G sont des rendez-vous exclusifs réservés aux membres du Club Henry & Gaby. Chaque événement est une occasion unique de networking haut de gamme dans des lieux prestigieux, autour de champagne millésimé, caviar et jazz live. Une signature d'élégance et de raffinement."
              : "H&G After-Works are exclusive meetings reserved for Henry & Gaby Club members. Each event is a unique opportunity for high-end networking in prestigious venues, around vintage champagne, caviar and live jazz. A signature of elegance and refinement."}
          </p>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedAfterwork(null);
        }}
        item={selectedAfterwork}
        bookingType="afterwork"
        onSuccess={() => {
          fetchAfterWorks();
        }}
      />
    </div>
  );
};

export default AfterWork;
